import React, { useState } from "react";
import { ExternalLink, Link2, RefreshCw } from "lucide-react";
import { toast } from "react-toastify";
import { v4 as uuid } from "uuid";
import api from "~/services/api";

const money = (value) =>
  value == null
    ? "Unknown"
    : Number(value).toLocaleString("en-US", { style: "currency", currency: "USD" });

const REASON = {
  wrong_amount: "Link amount differs from the balance",
  legacy_changed: "Old link, invoice changed after it was created",
  not_payable: "Invoice is not payable anymore",
};

export default function PaymentLinksRefresh() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [results, setResults] = useState({});
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [sending, setSending] = useState({});
  const [sent, setSent] = useState({});

  const invoiceIds = [...new Set(rows.map((row) => row.receivable_id))];
  const selectedCount = invoiceIds.filter((id) => selected.has(id)).length;

  async function check() {
    setLoading(true);
    try {
      const { data } = await api.get("/data-sync/gravity-links/stale");
      setRows(data?.rows || []);
      setSelected(new Set((data?.rows || []).map((row) => row.receivable_id)));
      setResults({});
      setChecked(true);
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to check payment links.", {
        type: "error",
        autoClose: 4000,
      });
    } finally {
      setLoading(false);
    }
  }

  function toggle(receivableId) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(receivableId)) next.delete(receivableId);
      else next.add(receivableId);
      return next;
    });
  }

  async function confirm() {
    if (!selectedCount) return;
    const confirmed = window.confirm(
      `Cancel the old payment link and create a new one for ${selectedCount} invoice(s)? Old links will stop working.`
    );
    if (!confirmed) return;
    setRunning(true);
    try {
      const { data } = await api.post("/data-sync/gravity-links/refresh", {
        receivable_ids: invoiceIds.filter((id) => selected.has(id)),
      });
      const byInvoice = {};
      (data?.results || []).forEach((result) => {
        byInvoice[result.receivable_id] = result;
      });
      setResults(byInvoice);
      const failed = (data?.results || []).filter((result) => result.status === "failed").length;
      toast(
        failed
          ? `${(data?.results || []).length - failed} done, ${failed} failed.`
          : "Payment links updated!",
        { type: failed ? "warning" : "success", autoClose: 4000 }
      );
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to update payment links.", {
        type: "error",
        autoClose: 5000,
      });
    } finally {
      setRunning(false);
    }
  }

  async function resend(receivableId, includeSms) {
    if (sending[receivableId]) return;
    setSending((current) => ({ ...current, [receivableId]: true }));
    try {
      const { data } = await api.post(`/receivables/send-invoice/${receivableId}`, {
        include_sms: includeSms,
        include_email: true,
        ...(includeSms ? { request_id: uuid() } : {}),
      });
      const result = data?.notifications;
      const accepted =
        result?.email?.status === "accepted" &&
        (!includeSms || result?.sms?.status === "accepted");
      setSent((current) => ({
        ...current,
        [receivableId]: accepted ? (includeSms ? "SMS + Email sent" : "Email sent") : "Check the invoice",
      }));
      toast(accepted ? "Invoice sent!" : "Check the sending result on the invoice.", {
        type: accepted ? "success" : "warning",
        autoClose: 4000,
      });
    } catch (err) {
      toast(err?.response?.data?.error || "Failed to send the invoice.", {
        type: "error",
        autoClose: 5000,
      });
    } finally {
      setSending((current) => ({ ...current, [receivableId]: false }));
    }
  }

  function sendCell(row) {
    const refreshed = results[row.receivable_id]?.status === "refreshed";
    if (sent[row.receivable_id]) {
      return <span className="text-green-700">{sent[row.receivable_id]}</span>;
    }
    return (
      <div className="flex gap-1">
        {[
          { label: "Email", sms: false },
          { label: "SMS + Email", sms: true },
        ].map((option) => (
          <button
            key={option.label}
            type="button"
            disabled={!refreshed || sending[row.receivable_id]}
            onClick={() => resend(row.receivable_id, option.sms)}
            className="border rounded-md px-2 h-6 text-[11px] font-semibold hover:border-primary hover:text-primary disabled:opacity-40"
          >
            {option.label}
          </button>
        ))}
      </div>
    );
  }

  function newLinkCell(row) {
    const result = results[row.receivable_id];
    if (result?.status === "failed") {
      return <span className="text-red-700">{result.error}</span>;
    }
    if (result?.status === "cancelled") {
      return <span className="text-gray-600">Cancelled</span>;
    }
    if (result?.status === "refreshed") {
      return result.payment_page_url ? (
        <a
          href={result.payment_page_url}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 text-green-700 underline"
        >
          <ExternalLink size={12} /> {money(result.link_amount)}
        </a>
      ) : (
        <span className="text-green-700">Created</span>
      );
    }
    return row.action === "replace" ? (
      <span className="text-gray-600">New link for {money(row.invoice_balance)}</span>
    ) : (
      <span className="text-gray-600">Will be cancelled</span>
    );
  }

  return (
    <div className="flex flex-col gap-3 border rounded-lg p-4 min-w-0">
      <p className="text-sm font-semibold">Gravity Payment Links</p>
      <p className="text-xs text-gray-500">
        Lists active Gravity - Online links that charge a different amount than the invoice balance.
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          disabled={loading || running}
          onClick={check}
          className="text-md font-bold bg-secondary border hover:border-primary hover:text-primary rounded-md px-4 h-8 flex items-center justify-center text-xs gap-2 disabled:opacity-50"
        >
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          {loading ? "Checking..." : "Check payment links"}
        </button>
        {rows.length > 0 && (
          <button
            type="button"
            disabled={loading || running || selectedCount === 0}
            onClick={confirm}
            className="text-md font-bold bg-primary text-white border rounded-md px-4 h-8 flex items-center justify-center text-xs gap-2 disabled:opacity-50"
          >
            <Link2 size={13} />
            {running ? "Updating..." : `Confirm and recreate (${selectedCount})`}
          </button>
        )}
      </div>

      {checked && rows.length === 0 && (
        <div className="rounded-lg border border-green-300 bg-green-50 p-3 text-xs text-green-800">
          No outdated payment links found.
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-auto border rounded-lg max-h-[28rem]">
          <table className="min-w-[1300px] w-full text-xs">
            <thead className="bg-gray-100 text-left sticky top-0">
              <tr>
                <th className="p-2">Sel.</th>
                <th className="p-2">Invoice</th>
                <th className="p-2">Registration</th>
                <th className="p-2">Student</th>
                <th className="p-2">Payment link</th>
                <th className="p-2">Invoice balance</th>
                <th className="p-2">Link balance</th>
                <th className="p-2">New link</th>
                <th className="p-2">Resend</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.link_id} className="border-t align-top">
                  <td className="p-2 text-center">
                    <input
                      type="checkbox"
                      checked={selected.has(row.receivable_id)}
                      onChange={() => toggle(row.receivable_id)}
                    />
                  </td>
                  <td className="p-2">
                    <div className="font-semibold">#{row.invoice_number}</div>
                    <div className="text-[10px] text-gray-500">{row.status}</div>
                  </td>
                  <td className="p-2">{row.registration_number}</td>
                  <td className="p-2">{row.student}</td>
                  <td className="p-2">
                    <a
                      href={row.payment_page_url}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-1 underline"
                    >
                      <ExternalLink size={12} /> Open
                    </a>
                    <div className="text-[10px] text-gray-500">{REASON[row.reason]}</div>
                  </td>
                  <td className="p-2">{money(row.invoice_balance)}</td>
                  <td className="p-2">{money(row.link_amount)}</td>
                  <td className="p-2">{newLinkCell(row)}</td>
                  <td className="p-2">{sendCell(row)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
